import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'

import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Typography,
    Chip,
    useTheme,
} from '@mui/material'
import { NewReleases, OpenInNew } from '@mui/icons-material'

import { FlexDiv } from '../../components'
import { ResponsivePageContainer } from '../../components/PageContainers'
import { getShortFormDate } from '../../helpers/stringHelpers'
import { useApplicantDocuments } from '../../hooks/useApplicantDocuments'
import { useDocuments } from '../../hooks/useDocuments'
import { useTenantLeasingDocuments } from '../../hooks/useLeaseOffers'
import { RootState } from '../../store'
import { TenantSharedDocument } from '../../types/companyDocument'

export const Documents = () => {
    const theme = useTheme()
    const selectedLease = useSelector(
        (state: RootState) => state.lease.selectedLease,
    )
    const leasingContext = useSelector(
        (state: RootState) => state.user.leasingContext,
    )
    const isApplicant = leasingContext?.mode === 'applicant'
    const apartmentId = selectedLease?.apartment_id
    const leadId = leasingContext?.active_lead_id

    const tenantDocs = useDocuments(apartmentId)
    const applicantDocs = useApplicantDocuments(leadId)
    const leasingDocs = useTenantLeasingDocuments()

    const leasingDocumentList = isApplicant
        ? applicantDocs.documentList
        : leasingDocs.documentList

    const isLoading = isApplicant
        ? applicantDocs.isLoading
        : tenantDocs.isLoading || leasingDocs.isLoading

    useEffect(() => {
        if (isApplicant) {
            applicantDocs.getDocumentList()
            return
        }
        tenantDocs.getDocumentList()
        leasingDocs.getDocumentList()
    }, [apartmentId, isApplicant, leadId])

    const handleViewSharedDocument = (doc: TenantSharedDocument) => {
        if (!doc.tenant_viewed) {
            tenantDocs.markDocumentAsViewed(doc.id)
        }
        if (doc.document) {
            window.open(doc.document, '_blank')
        }
    }

    const handleOpenApplicantDocument = (doc: {
        signing_url: string | null
        signed_pdf_url: string | null
        can_sign: boolean
        can_view: boolean
    }) => {
        const href = doc.can_view
            ? doc.signed_pdf_url
            : doc.can_sign
              ? doc.signing_url
              : null
        if (href) {
            window.open(href, '_blank', 'noopener,noreferrer')
        }
    }

    if (!isApplicant && !apartmentId) {
        return (
            <ResponsivePageContainer
                title="Documents"
                subtitle="View documents shared by your property manager."
            >
                <Typography color="textSecondary">
                    Select a lease to view shared documents.
                </Typography>
            </ResponsivePageContainer>
        )
    }

    return (
        <ResponsivePageContainer
            title="Documents"
            subtitle={
                isApplicant
                    ? 'Completed application, lease offer, and documents to sign.'
                    : 'Lease offers, signed packets, and documents shared by your property manager.'
            }
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    width: '100%',
                    alignItems: 'flex-start',
                }}
            >
                {isLoading ? (
                    <FlexDiv
                        style={{
                            justifyContent: 'center',
                            padding: theme.spacing(4),
                            width: '100%',
                        }}
                    >
                        <CircularProgress
                            size={40}
                            style={{ color: theme.palette.primary.main }}
                        />
                    </FlexDiv>
                ) : (
                    <Grid
                        container
                        spacing={2}
                        sx={{
                            maxWidth: '800px',
                            width: '100%',
                            alignItems: 'flex-start',
                        }}
                    >
                        {(isApplicant
                            ? applicantDocs.documentList.length === 0
                            : tenantDocs.documentList.length === 0 &&
                              leasingDocumentList.length === 0) ? (
                            <Grid item xs={12}>
                                <Card
                                    sx={{
                                        bgcolor: 'rgba(138, 142, 140, 0.16)',
                                        borderRadius: 2,
                                        boxShadow: 'none',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        p: 3,
                                        textAlign: 'center',
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{ color: 'rgba(255,255,255,0.8)', mb: 1 }}
                                    >
                                        No documents yet
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{ color: 'rgba(255,255,255,0.5)' }}
                                    >
                                        {isApplicant
                    ? 'Your completed application, lease offer, and lease packet will show up here.'
                    : 'Your renewal offer, signed responses, and shared documents will show up here.'}
                                    </Typography>
                                </Card>
                            </Grid>
                        ) : (
                            <>
                            {leasingDocumentList.map((doc) => (
                                <Grid item xs={12} key={`${doc.source ?? 'signing'}-${doc.id}`}>
                                    <Card
                                        sx={{
                                            bgcolor: 'rgba(138, 142, 140, 0.16)',
                                            borderRadius: 2,
                                            boxShadow: 'none',
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            width: '100%',
                                            height: 'fit-content',
                                        }}
                                    >
                                        <CardContent
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'space-between',
                                                gap: 2,
                                                py: 1.5,
                                                px: 2,
                                                '&:last-child': {
                                                    pb: 1.5,
                                                },
                                            }}
                                        >
                                            <Box sx={{ minWidth: 0 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        flexWrap: 'wrap',
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: 'rgba(255,255,255,0.9)',
                                                            lineHeight: 1.3,
                                                        }}
                                                    >
                                                        {doc.name}
                                                    </Typography>
                                                    <Chip
                                                        size="small"
                                                        icon={
                                                            doc.can_sign ? (
                                                                <NewReleases />
                                                            ) : undefined
                                                        }
                                                        label={
                                                            doc.source === 'application'
                                                                ? 'Application'
                                                                : doc.source === 'offer'
                                                                ? 'Offer'
                                                                : doc.source === 'offer_response'
                                                                ? 'Response'
                                                                : doc.can_view
                                                                ? 'Signed'
                                                                : doc.can_sign
                                                                  ? 'To sign'
                                                                  : 'Sent'
                                                        }
                                                        color={
                                                            doc.can_sign
                                                                ? 'primary'
                                                                : 'default'
                                                        }
                                                        sx={{ flexShrink: 0 }}
                                                    />
                                                </Box>

                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'rgba(255,255,255,0.5)',
                                                    }}
                                                >
                                                    {doc.document_type_label}
                                                </Typography>

                                                {doc.sent_at && (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: 'rgba(255,255,255,0.5)',
                                                        }}
                                                    >
                                                        Sent{' '}
                                                        {getShortFormDate(
                                                            doc.sent_at,
                                                        )}
                                                    </Typography>
                                                )}
                                            </Box>

                                            <Button
                                                variant="outlined"
                                                size="small"
                                                color="primary"
                                                endIcon={<OpenInNew />}
                                                disabled={
                                                    !doc.can_sign && !doc.can_view
                                                }
                                                onClick={() =>
                                                    handleOpenApplicantDocument(
                                                        doc,
                                                    )
                                                }
                                                sx={{
                                                    flexShrink: 0,
                                                    borderColor:
                                                        'rgba(255,255,255,0.2)',
                                                    color: 'rgba(255,255,255,0.85)',
                                                    '&:hover': {
                                                        borderColor:
                                                            theme.palette.primary
                                                                .main,
                                                        backgroundColor:
                                                            'rgba(255,255,255,0.05)',
                                                    },
                                                }}
                                            >
                                                {doc.can_sign ? 'Sign' : 'View'}
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                            {!isApplicant &&
                                tenantDocs.documentList.map((doc) => (
                                <Grid item xs={12} key={`shared-${doc.id}`}>
                                    <Card
                                        sx={{
                                            bgcolor: 'rgba(138, 142, 140, 0.16)',
                                            borderRadius: 2,
                                            boxShadow: 'none',
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            width: '100%',
                                            height: 'fit-content',
                                        }}
                                    >
                                        <CardContent
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'space-between',
                                                gap: 2,
                                                py: 1.5,
                                                px: 2,
                                                '&:last-child': {
                                                    pb: 1.5,
                                                },
                                            }}
                                        >
                                            <Box sx={{ minWidth: 0 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        flexWrap: 'wrap',
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: 'rgba(255,255,255,0.9)',
                                                            lineHeight: 1.3,
                                                        }}
                                                    >
                                                        {doc.name}
                                                    </Typography>
                                                    {!doc.tenant_viewed && (
                                                        <Chip
                                                            size="small"
                                                            icon={<NewReleases />}
                                                            label="New"
                                                            color="primary"
                                                            sx={{ flexShrink: 0 }}
                                                        />
                                                    )}
                                                </Box>

                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'rgba(255,255,255,0.5)',
                                                    }}
                                                >
                                                    {doc.document_type_detail?.name ??
                                                        'General'}
                                                </Typography>

                                                {doc.shared_at && (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: 'rgba(255,255,255,0.5)',
                                                        }}
                                                    >
                                                        Shared{' '}
                                                        {getShortFormDate(
                                                            doc.shared_at,
                                                        )}
                                                    </Typography>
                                                )}
                                            </Box>

                                            <Button
                                                variant="outlined"
                                                size="small"
                                                color="primary"
                                                endIcon={<OpenInNew />}
                                                onClick={() =>
                                                    handleViewSharedDocument(doc)
                                                }
                                                sx={{
                                                    flexShrink: 0,
                                                    borderColor:
                                                        'rgba(255,255,255,0.2)',
                                                    color: 'rgba(255,255,255,0.85)',
                                                    '&:hover': {
                                                        borderColor:
                                                            theme.palette.primary
                                                                .main,
                                                        backgroundColor:
                                                            'rgba(255,255,255,0.05)',
                                                    },
                                                }}
                                            >
                                                View
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                            </>
                        )}
                    </Grid>
                )}
            </Box>
        </ResponsivePageContainer>
    )
}
